import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getSocket } from '@app/services/socketService';

export interface OrderNotification {
  id: string;
  orderId: number;
  customerName: string;
  amount: number;
  type: string;
  createdAt: string;
  isRead: boolean;
}

interface OrderNotificationContextType {
  notifications: OrderNotification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
}

const OrderNotificationContext = createContext<OrderNotificationContextType>({
  notifications: [],
  unreadCount: 0,
  markAsRead: () => {},
  markAllAsRead: () => {},
});

export const useOrderNotifications = () => useContext(OrderNotificationContext);

const STORAGE_KEY = 'order_notifications';
const MAX_NOTIFICATIONS = 50;

const loadNotifications = (): OrderNotification[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveNotifications = (notifications: OrderNotification[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
};

export const OrderNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<OrderNotification[]>(loadNotifications);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio('/sounds/notification.wav');
    audioRef.current.volume = 0.7;
  }, []);

  // Play notification sound
  const playSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err) => {
        console.warn('Could not play notification sound:', err.message);
      });
    }
  }, []);

  // Listen for new-order events
  useEffect(() => {
    const socket = getSocket();

    const handleNewOrder = (data: any) => {
      const newNotification: OrderNotification = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        orderId: data.orderId,
        customerName: data.customerName || 'Unknown',
        amount: data.amount || 0,
        type: data.type || 'A La Carte',
        createdAt: data.createdAt || new Date().toISOString(),
        isRead: false,
      };

      setNotifications((prev) => {
        const updated = [newNotification, ...prev].slice(0, MAX_NOTIFICATIONS);
        saveNotifications(updated);
        return updated;
      });

      playSound();
    };

    socket.on('new-order', handleNewOrder);

    return () => {
      socket.off('new-order', handleNewOrder);
    };
  }, [playSound]);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) => {
      const updated = prev.map((n) => (n.id === id ? { ...n, isRead: true } : n));
      saveNotifications(updated);
      return updated;
    });
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => {
      const updated = prev.map((n) => ({ ...n, isRead: true }));
      saveNotifications(updated);
      return updated;
    });
  }, []);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <OrderNotificationContext.Provider value={{ notifications, unreadCount, markAsRead, markAllAsRead }}>
      {children}
    </OrderNotificationContext.Provider>
  );
};
